// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Ingest. People arrive holding whatever their code was about to POST, so the
 * parser accepts all the shapes that actually occur rather than demanding one:
 *
 *   • an OpenAI request body        { model?, messages, tools }
 *   • a bare array of messages      [ {role, content}, … ]
 *   • an Anthropic request body     { system, messages, tools:[{name, input_schema}] }
 *   • our own sample file           { messages, tools, _comment }
 *
 * Anthropic's shape needs real work rather than a cast: its `system` is a
 * top-level string (or array of content blocks) rather than a message, and its
 * tools carry `input_schema` where the open models' templates expect
 * `function.parameters`. Both are normalised here so the rest of the app only
 * ever sees one shape.
 */

import type { ChatMessage, Conversation, ToolDefinition } from './types';

export class ParseError extends Error {
  constructor(
    message: string,
    public readonly hint?: string,
  ) {
    super(message);
    this.name = 'ParseError';
  }
}

const ROLES = new Set(['system', 'user', 'assistant', 'tool', 'function', 'developer']);

function asText(v: unknown): string {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    return v
      .map((p) => (typeof p === 'string' ? p : typeof (p as { text?: string })?.text === 'string' ? (p as { text: string }).text : ''))
      .join('\n');
  }
  return '';
}

/** Anthropic tools use { name, description, input_schema }. */
function normaliseTool(t: unknown): ToolDefinition | null {
  if (!t || typeof t !== 'object') return null;
  const o = t as Record<string, unknown>;
  if (o.function && typeof o.function === 'object') return o as ToolDefinition;
  if (typeof o.name === 'string') {
    return {
      type: 'function',
      function: {
        name: o.name,
        description: typeof o.description === 'string' ? o.description : undefined,
        parameters: o.input_schema ?? o.parameters ?? { type: 'object', properties: {} },
      },
    };
  }
  return null;
}

function normaliseMessage(m: unknown): ChatMessage | null {
  if (!m || typeof m !== 'object') return null;
  const o = m as Record<string, unknown>;
  const role = typeof o.role === 'string' ? o.role : null;
  if (!role) return null;

  const out: ChatMessage = { role, ...o } as ChatMessage;

  // Anthropic represents a tool result as a user message containing a
  // tool_result block. Templates for open models expect role "tool".
  if (Array.isArray(o.content)) {
    const blocks = o.content as Array<Record<string, unknown>>;
    const toolResult = blocks.find((b) => b?.type === 'tool_result');
    if (toolResult) {
      return {
        role: 'tool',
        tool_call_id: typeof toolResult.tool_use_id === 'string' ? toolResult.tool_use_id : undefined,
        content: asText(toolResult.content),
      };
    }
    const toolUse = blocks.filter((b) => b?.type === 'tool_use');
    if (toolUse.length) {
      return {
        role,
        content: asText(blocks.filter((b) => b?.type === 'text')),
        tool_calls: toolUse.map((b) => ({
          id: typeof b.id === 'string' ? b.id : undefined,
          type: 'function',
          function: {
            name: String(b.name ?? 'tool'),
            arguments: typeof b.input === 'string' ? b.input : JSON.stringify(b.input ?? {}),
          },
        })),
      };
    }
  }
  return out;
}

export function parseConversation(raw: string): Conversation {
  const trimmed = raw.trim();
  if (!trimmed) throw new ParseError('Nothing to parse.', 'Paste a messages array or an API request body.');

  let data: unknown;
  try {
    data = JSON.parse(trimmed);
  } catch (e) {
    throw new ParseError(
      `That is not valid JSON: ${(e as Error).message}`,
      trimmed.startsWith('{') || trimmed.startsWith('[')
        ? 'Check for a trailing comma or an unescaped quote.'
        : 'Paste the JSON body itself, not a curl command or a code snippet around it.',
    );
  }

  let messagesRaw: unknown[] = [];
  let toolsRaw: unknown[] = [];
  let systemPrefix: ChatMessage | null = null;

  if (Array.isArray(data)) {
    messagesRaw = data;
  } else if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.messages)) messagesRaw = o.messages;
    else if (Array.isArray(o.conversation)) messagesRaw = o.conversation;
    else {
      throw new ParseError(
        'No `messages` array found.',
        'chatprint expects an OpenAI or Anthropic request body, or a bare array of messages.',
      );
    }
    if (Array.isArray(o.tools)) toolsRaw = o.tools;
    else if (Array.isArray(o.functions)) toolsRaw = o.functions;

    if (o.system) {
      const text = asText(o.system);
      if (text) systemPrefix = { role: 'system', content: text };
    }
  } else {
    throw new ParseError('Expected a JSON object or array.');
  }

  const messages = messagesRaw.map(normaliseMessage).filter((m): m is ChatMessage => m !== null);
  if (systemPrefix && !messages.some((m) => m.role === 'system')) messages.unshift(systemPrefix);

  if (messages.length === 0) {
    throw new ParseError('The messages array is empty or contains no objects with a `role`.');
  }

  const unknownRoles = [...new Set(messages.map((m) => String(m.role)))].filter((r) => !ROLES.has(r));
  if (unknownRoles.length) {
    // Not fatal — some templates handle custom roles, most silently drop them,
    // and showing that they were dropped is a feature rather than an error.
    messages.forEach((m) => void m);
  }

  const tools = toolsRaw.map(normaliseTool).filter((t): t is ToolDefinition => t !== null);

  return { messages, tools };
}

/** Roles present that most templates will silently discard. */
export function unusualRoles(messages: ChatMessage[]): string[] {
  return [...new Set(messages.map((m) => String(m.role)))].filter((r) => !['system', 'user', 'assistant', 'tool'].includes(r));
}
